import type { PlaygroundRunOutputResponse } from "contracts/api/playground";
import { PAGE_ANALYSIS_STATUS } from "contracts/enums/page-analysis";
import { For, Show } from "solid-js";
import { YEET_FAILURE_REASON_LABELS } from "../../enums/run-failures";
import { api } from "../../store/api";
import { AuthenticatedImage } from "../../ui/AuthenticatedImage";
import { elapsedDuration, relativeAge } from "../../ui/format";

type Attempt = NonNullable<PlaygroundRunOutputResponse["attempt"]>;
type Screenshot = PlaygroundRunOutputResponse["screenshots"][number];

/** The "what the agent did" half of a run: sandbox attempt telemetry (exit,
 * logs, summary) and the desktop/mobile captures it took. */
export function RunTelemetry(props: {
  attempt: PlaygroundRunOutputResponse["attempt"];
  screenshots: Screenshot[];
  runId: string;
}) {
  return (
    <>
      <Show when={props.attempt}>
        {(attempt) => <AttemptPanel attempt={attempt()} />}
      </Show>
      <Show when={props.screenshots.length > 0}>
        <section class="panel">
          <div class="panel-head">
            <span class="panel-title">Screenshots</span>
          </div>
          <div class="panel-body is-row">
            <For each={props.screenshots}>
              {(shot) => <ScreenshotFigure shot={shot} runId={props.runId} />}
            </For>
          </div>
        </section>
      </Show>
    </>
  );
}

function AttemptPanel(props: { attempt: Attempt }) {
  const attempt = () => props.attempt;
  return (
    <section class="panel">
      <div class="panel-head">
        <span class="panel-title">Agent run</span>
        <span class="badge">{attempt().status}</span>
        <Show when={attempt().failureReason}>
          {(reason) => (
            <span class="badge-sev sev-critical">
              {YEET_FAILURE_REASON_LABELS[reason()]}
            </span>
          )}
        </Show>
      </div>
      <div class="panel-body">
        <p class="landing-sub">
          exit {attempt().exitCode ?? "—"} · sandbox{" "}
          <span class="mono">{attempt().sandboxId ?? "—"}</span> · ran{" "}
          {elapsedDuration(attempt().createdAt, attempt().finishedAt)}
          {attempt().finishedAt ? "" : " so far"} · started{" "}
          {relativeAge(attempt().createdAt)}
        </p>
        <p class="landing-sub">
          <Show when={attempt().loopUsage}>
            {(usage) => (
              <>
                {usage().steps} steps · {usage().inputTokens.toLocaleString()}{" "}
                tokens in / {usage().outputTokens.toLocaleString()} out ·{" "}
              </>
            )}
          </Show>
          capture <span class="mono">{attempt().captureId}</span>
        </p>
        <Show when={attempt().summary}>
          <p>{attempt().summary}</p>
        </Show>
        <Show when={attempt().error}>
          <p class="badge-sev sev-critical">{attempt().error}</p>
        </Show>
        <Show when={attempt().stderrTail}>
          <details>
            <summary>Diagnostic log (stderr)</summary>
            <pre class="mono">{attempt().stderrTail}</pre>
          </details>
        </Show>
        <Show when={attempt().stdoutTail}>
          <details>
            <summary>Agent log (stdout)</summary>
            <pre class="mono">{attempt().stdoutTail}</pre>
          </details>
        </Show>
      </div>
    </section>
  );
}

function ScreenshotFigure(props: { shot: Screenshot; runId: string }) {
  const shot = () => props.shot;
  return (
    <figure class="shot-figure">
      <figcaption>
        <span class="badge">{shot().device}</span>{" "}
        <Show when={shot().error}>
          <span class="badge-sev sev-medium">{shot().error}</span>
        </Show>
      </figcaption>
      <Show when={shot().status === PAGE_ANALYSIS_STATUS.COMPLETED}>
        <AuthenticatedImage
          path={api.playground.screenshotPath(props.runId, shot().id)}
          alt={`${shot().device} capture`}
        />
      </Show>
    </figure>
  );
}
